/* ------------------------------------------------------------------ *
 * Business colour palettes.
 *
 * A palette is three seed colours — `primary` (buttons, links, active
 * states, print accents), `accent` (the secondary series in charts, the
 * teal chips) and `header` (the top-bar band). Everything else the UI
 * needs — hover, tints, hairlines, the cream glow behind the logo — is
 * derived from those seeds, so a business picking a custom colour gets a
 * complete, consistent set without anyone hand-tuning ten shades.
 *
 * Presets may pin any derived token explicitly; Heritage gold does, so
 * the default look stays pixel-identical to the hand-tuned original.
 *
 * The tokens are written to :root as --rf-brand-* custom properties by
 * ThemeRoot (see ./BrandingProvider.js). Every stylesheet, modal and
 * print page reads those, so a palette change lands everywhere at once.
 * ------------------------------------------------------------------ */

/* ---------- colour maths ---------- */

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

export function isHex(value) {
  return /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(String(value || "").trim());
}

export function normHex(value) {
  let h = String(value || "").trim().toLowerCase();
  if (!isHex(h)) return null;
  if (h.length === 4) h = "#" + h[1] + h[1] + h[2] + h[2] + h[3] + h[3];
  return h;
}

export function hexToRgb(hex) {
  const h = normHex(hex);
  if (!h) return null;
  return [
    parseInt(h.slice(1, 3), 16),
    parseInt(h.slice(3, 5), 16),
    parseInt(h.slice(5, 7), 16),
  ];
}

const toHex2 = (n) => Math.round(clamp(n, 0, 255)).toString(16).padStart(2, "0");
const rgbToHex = ([r, g, b]) => `#${toHex2(r)}${toHex2(g)}${toHex2(b)}`;

function rgbToHsl([r, g, b]) {
  r /= 255;
  g /= 255;
  b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l * 100];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return [h * 60, s * 100, l * 100];
}

function hslToRgb([h, s, l]) {
  s = clamp(s, 0, 100) / 100;
  l = clamp(l, 0, 100) / 100;
  const k = (n) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
  return [f(0) * 255, f(8) * 255, f(4) * 255];
}

/* Same hue as `hex`, saturation scaled by `sat`, lightness set to `light`
   (absolute) or shifted by `shift` (relative to the seed). */
function shade(hex, { light, shift, sat = 1 }) {
  const [h, s, l] = rgbToHsl(hexToRgb(hex));
  const nextL = light !== undefined ? light : l + (shift || 0);
  return rgbToHex(hslToRgb([h, clamp(s * sat, 0, 100), clamp(nextL, 0, 100)]));
}

/* WCAG relative luminance / contrast, for picking legible ink on a colour. */
function luminance(hex) {
  const lin = hexToRgb(hex).map((c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
}

export function contrast(a, b) {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/* ---------- presets ---------- */

export const DEFAULT_PRESET_ID = "heritage-gold";

export const PRESETS = [
  {
    id: "heritage-gold",
    name: "Heritage gold",
    primary: "#8f7136",
    accent: "#3f6f6a",
    header: "#8e7036",
    /* the hand-tuned originals — keep the default look exact */
    pinned: {
      "brand-hover": "#785d2a",
      "brand-deep": "#6b5629",
      "brand-lite": "#b79856",
      "brand-soft": "#c9ac74",
      "brand-pale": "#dcc79a",
      "brand-bright": "#e8c98a",
      "brand-line": "#e6dcc6",
      "brand-edge": "#ece0c6",
      "brand-tint": "#f7f1e3",
      "brand-wash": "#fdfaf3",
      "brand-night": "#3a2f1c",
      "accent-tint": "#eef4f3",
      "header-cream": "#ece2cb",
    },
  },
  { id: "eucalyptus", name: "Eucalyptus", primary: "#3d6b63", accent: "#a0703a", header: "#35605a" },
  { id: "plum", name: "Plum", primary: "#7a5b7d", accent: "#5b7a6a", header: "#6c4f6f" },
  { id: "olive-grove", name: "Olive grove", primary: "#6b6f3f", accent: "#8a5a3c", header: "#5f6338" },
  { id: "terracotta", name: "Terracotta", primary: "#a65a3a", accent: "#3f6f6a", header: "#944f32" },
  { id: "harbour", name: "Harbour navy", primary: "#34507a", accent: "#b0872f", header: "#2c4468" },
  { id: "dusty-rose", name: "Dusty rose", primary: "#a3566b", accent: "#4d6a8a", header: "#924b5f" },
  { id: "charcoal", name: "Charcoal", primary: "#4a453c", accent: "#8f7136", header: "#2f2b25" },
];

export const presetById = (id) => PRESETS.find((p) => p.id === id) || null;

/* ---------- derivation ---------- */

/**
 * Every token the UI reads, from three seeds (+ optional pinned values).
 * Keys match the CSS custom properties without the `--rf-` prefix.
 */
export function deriveTokens({ primary, accent, header, pinned = {} }) {
  const P = normHex(primary) || PRESETS[0].primary;
  const A = normHex(accent) || PRESETS[0].accent;
  const H = normHex(header) || P;

  const t = {
    brand: P,
    "brand-hover": shade(P, { shift: -7, sat: 1.07 }),
    "brand-deep": shade(P, { shift: -10 }),
    "brand-lite": shade(P, { light: 53, sat: 0.92 }),
    "brand-soft": shade(P, { light: 62 }),
    "brand-pale": shade(P, { light: 73, sat: 1.1 }),
    "brand-bright": shade(P, { light: 72, sat: 1.5 }),
    "brand-line": shade(P, { light: 84, sat: 0.9 }),
    "brand-edge": shade(P, { light: 85, sat: 1.12 }),
    "brand-tint": shade(P, { light: 93, sat: 1.3 }),
    "brand-wash": shade(P, { light: 97, sat: 1.5 }),
    /* area fill under a line on the dark overview charts */
    "brand-night": shade(P, { light: 17, sat: 0.78 }),
    accent: A,
    "accent-tint": shade(A, { light: 94, sat: 0.5 }),
    header: H,
    "header-cream": shade(H, { light: 86, sat: 1.1 }),
    ...pinned,
  };
  /* the few colours used inside rgba(): expose "r, g, b" triplets */
  ["brand", "brand-lite", "brand-tint", "header-cream", "accent"].forEach((k) => {
    t[`${k}-rgb`] = hexToRgb(t[k]).join(", ");
  });
  return t;
}

/** A branding theme ({ preset, primary?, accent?, header? }) -> tokens. */
export function tokensForTheme(theme) {
  const th = theme || {};
  // "custom" (or an unknown id) fills any missing seed from the default
  const preset = presetById(th.preset) || PRESETS[0];
  const primary = normHex(th.primary) || preset.primary;
  const accent = normHex(th.accent) || preset.accent;
  const header = normHex(th.header) || preset.header;
  // pinned shades only belong to an untouched preset
  const untouched =
    primary === preset.primary && accent === preset.accent && header === preset.header;
  return deriveTokens({ primary, accent, header, pinned: untouched ? preset.pinned : undefined });
}

/** tokens -> { "--rf-brand": "#…", … } for a style prop or setProperty. */
export function cssVarsFor(tokens) {
  const out = {};
  Object.keys(tokens).forEach((k) => {
    out[`--rf-${k}`] = tokens[k];
  });
  return out;
}

/** A run of shades for chart series (donuts, stacked bars). */
export function seriesFor(tokens) {
  return [tokens.brand, tokens["brand-lite"], tokens["brand-soft"], tokens["brand-pale"], tokens["brand-edge"]];
}
